import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LeadModalProvider } from "@/context/LeadModalProvider";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { PricingProvider } from "@/context/PricingProvider";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Chatbot } from "@/components/Chatbot";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import ServiceAreas from "@/pages/ServiceAreas";
import ServiceAreaDetail from "@/pages/ServiceAreaDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import TermsPage from "@/pages/TermsPage";
import ServiceTermsPage from "@/pages/ServiceTermsPage";
import ServiceAgreementPage from "@/pages/ServiceAgreementPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import DepositSuccess from "@/pages/DepositSuccess";
import DepositCancel from "@/pages/DepositCancel";
import NotFound from "@/pages/NotFound";
import CapabilityStatement from "@/pages/CapabilityStatement";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
      return;
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

// Marketing site chrome (navbar / footer / chatbot) — hidden on standalone pages.
function MarketingLayout() {
  return (
    <>
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/service-areas" element={<ServiceAreas />} />
          <Route path="/service-areas/:slug" element={<ServiceAreaDetail />} />
          <Route path="/our-story" element={<About />} />
          <Route path="/capability-statement" element={<CapabilityStatement />} />
          <Route path="/about" element={<Navigate to="/our-story" replace />} />
          <Route path="/services/move-in-cleaning" element={<Navigate to="/services/move-in-move-out-cleaning" replace />} />
          <Route path="/services/move-out-cleaning" element={<Navigate to="/services/move-in-move-out-cleaning" replace />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/tip" element={<Navigate to="/" replace />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/service-terms" element={<ServiceTermsPage />} />
          <Route path="/service-agreement" element={<ServiceAgreementPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <Chatbot />
    </>
  );
}

function AppRoutes() {
  const { pathname } = useLocation();
  const standalone = pathname.startsWith("/admin") || pathname.startsWith("/booking/deposit");

  useEffect(() => {
    // Lenis smooth-scroll only on the marketing site (interferes with app screens).
    if (standalone) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, [standalone]);

  return (
    <>
      <ScrollManager />
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/booking/deposit/success" element={<DepositSuccess />} />
        <Route path="/booking/deposit/cancel" element={<DepositCancel />} />
        <Route path="/*" element={<MarketingLayout />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AdminAuthProvider>
          <PricingProvider>
            <LeadModalProvider>
              <AppRoutes />
              <Toaster position="top-center" richColors />
            </LeadModalProvider>
          </PricingProvider>
        </AdminAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
